import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseStorage';
import { useAppStore } from '../store/useAppStore';
import { useProMaxModels } from '../hooks/useProMaxModels';
import { FallbackScreen } from '../components/FallbackScreen';
import { logAnalytics } from '../utils/database';
import { AlertCircle, Search, Sparkles } from 'lucide-react';
import * as Icons from 'lucide-react';

export const UnleashPage = () => {
  const navigate = useNavigate();
  const { setSession: setGlobalSession, session: storeSession } = useAppStore();
  const { models, loading: modelsLoading } = useProMaxModels(true);

  const [session, setSession] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authCompleted, setAuthCompleted] = useState(false);
  const [unleashAppId, setUnleashAppId] = useState<string | null>(null);
  const [unleashPageLoading, setUnleashPageLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('pages').select('app_id').eq('route_path', '/unleash').maybeSingle();
      const id = data?.app_id?.trim() || null;
      setUnleashAppId(id);
      setUnleashPageLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (unleashPageLoading) return;

    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setAuthError(null);

        await logAnalytics('unleash_page_access', {
          timestamp: new Date().toISOString(),
          url: window.location.href
        });

        if (!storeSession) {
          await logAnalytics('unleash_page_session_missing', {
            error: 'No Supabase session',
          });
          setAuthError('Please sign in to use this page.');
          setIsAuthenticated(false);
          return;
        }

        if (!unleashAppId) {
          setAuthError(
            'Unleash has no App ID — create a Page with route /unleash and set App ID in Page Settings.'
          );
          return;
        }

        setSession(storeSession);
        setIsAuthenticated(true);
        setGlobalSession(storeSession);

        await logAnalytics('unleash_page_session_established', {
          user_id: storeSession.hy_user_id,
          plan: storeSession.hy_plan_dev_name
        });
      } catch (error) {
        console.error('Unleash page auth error:', error);
        await logAnalytics('unleash_page_auth_error', {
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        setAuthError('Failed to authenticate - Please try again');
      } finally {
        setIsLoading(false);
        setAuthCompleted(true);
      }
    };

    initializeAuth();
  }, [setGlobalSession, unleashPageLoading, unleashAppId, storeSession]);


  const categories = [
    { id: 'all', label: 'All Models', icon: Sparkles },
    { id: 'image', label: 'Image', icon: Icons.Image },
    { id: 'video', label: 'Video', icon: Icons.Video },
    { id: 'audio', label: 'Audio', icon: Icons.Music },
    { id: 'text', label: 'Text', icon: Icons.FileText },
  ];


  const filteredModels = models.filter(model => {
    const matchesSearch = model.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         model.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         model.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || model.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });


  const handleModelClick = (modelId: string) => {
    logAnalytics('unleash_model_selected', {
      user_id: session?.hy_user_id,
      model_id: modelId
    });
    const encodedModelId = encodeURIComponent(modelId);
    navigate(`/promax/${encodedModelId}`);
  };

  const getIconComponent = (iconName: string) => {
    const IconComponent = (Icons as any)[iconName] || Icons.Zap;
    return IconComponent;
  };

  if (unleashPageLoading || isLoading || !authCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <img
            src="https://qsiwkqsdtbfftmxbnhie.supabase.co/storage/v1/object/public/public/c599a7b3-23d3-4f3a-a44b-b1801b7cdf24.gif"
            alt="Loading..."
            className="w-[500px] h-[500px] mx-auto rounded-lg"
          />
        </div>
      </div>
    );
  }

  if (authError || !isAuthenticated || !session) {
    return <FallbackScreen />;
  }

  return (
    <div className="min-h-screen">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-th-secondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search models..."
              className="w-full pl-12 pr-4 py-3 bg-elevated/50 border border-subtle rounded-lg text-th-primary placeholder-th-tertiary focus:outline-none focus:bg-elevated focus:border-subtle transition-colors"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex gap-2 min-w-max pb-2">
            {categories.map((category) => {
              const Icon = category.icon;
              const isActive = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                    isActive
                      ? 'bg-brand text-surface'
                      : 'bg-transparent text-th-secondary hover:text-th-primary hover:bg-overlay/50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {category.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* All Models Grid */}
        <div className="pb-12">
          {modelsLoading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-brand border-t-transparent"></div>
              <p className="text-th-secondary mt-4">Loading models...</p>
            </div>
          ) : filteredModels.length === 0 ? (
            <div className="text-center py-16">
              <AlertCircle className="w-12 h-12 text-th-secondary mx-auto mb-3" />
              <p className="text-th-primary font-medium mb-1">No models found</p>
              <p className="text-sm text-th-secondary">Try adjusting your search or category</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredModels.map((model) => {
                const Icon = getIconComponent(model.icon_name);
                return (
                  <button
                    key={model.id}
                    onClick={() => handleModelClick(model.model_id)}
                    className="group bg-transparent hover:bg-overlay/30 border border-transparent hover:border-subtle rounded-xl overflow-hidden transition-all text-left"
                  >
                    {/* Compact Preview */}
                    <div className="aspect-video bg-elevated/50 relative overflow-hidden rounded-t-xl">
                      <div className="absolute inset-0 flex items-center justify-center bg-elevated">
                        <Icon className={`w-10 h-10 ${model.icon_color} opacity-80 group-hover:scale-110 transition-transform`} />
                      </div>
                    </div>

                    {/* Compact Info */}
                    <div className="p-3">
                      <h3 className="font-medium text-th-primary group-hover:text-brand transition-colors truncate text-sm mb-1">
                        {model.name}
                      </h3>
                      <p className="text-xs text-th-secondary line-clamp-2 mb-2">
                        {model.description}
                      </p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-th-secondary flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          {model.credit_cost}
                        </span>
                        <span className="text-th-secondary capitalize">
                          {model.category}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
